import { ds } from "js/core/models/dataService";
import { $, _ } from "js/vendor";
import { app } from "js/namespaces";
import * as geom from "js/core/utilities/geom";
import { WidgetPositionType } from "common/constants";
import { BaseElement } from "js/elements/base/BaseElement";
import { CollectionElement, CollectionItemElement } from "js/elements/base/CollectionElement";
import { TextElement } from "js/elements/base/TextElement";
import { TextGroup } from "js/elements/base/TextGroup";
import { Key } from "../../core/utilities/keys";
import { ElementRollover, ElementSelection } from "js/editor/elementUI/BaseElementEditor";
import { controls } from "js/editor/ui";
import { ContentElement } from "js/elements/base/ContentElement";
import { ANIMATION_DURATION } from "js/core/utilities/extensions";
import { ShowWarningDialog } from "js/react/components/Dialogs/BaseDialog";

export const CollectionElementSelection = ElementSelection.extend({
    renderControls: function() {
        this.createAddItemButton();
    },

    getAddItemLabel: function() {
        return "Add Item";
    },

    createAddItemButton: function(config = {}) {
        this.addControl({
            type: controls.BUTTON,
            label: this.getAddItemLabel(),
            icon: "add_circle",
            enabled: this.element.maxItemCount ? (this.element.itemCount < this.element.maxItemCount) : true,
            callback: () => {
                this.addItem({}, true)
                    .then(item => {
                        if (item) {
                            if (config.callback) {
                                config.callback(item);
                            } else if (!config.skipTextSelection) {
                                this.selectNewItemText(item.id);
                            }
                        }
                    });
            }
        });
    },

    selectNewItemText(path) {
        function findTextItem(children) {
            let text;
            text = _.find(children, child => {
                return child instanceof TextGroup && child.title && child.title.canEdit;
            });
            if (text) {
                text = text.title;
            } else {
                text = _.find(children, child => {
                    return child instanceof TextElement && child.canEdit;
                });
            }
            if (text) {
                return text;
            } else if (Object.keys(children)) {
                _.find(children, child => {
                    if (child.elements) {
                        text = findTextItem(child.elements);
                        return text;
                    }
                });
                return text;
            }
        }

        // try to select a new item's textGroup title, otherwise find a new item's first editable textElement
        // if no text is found, follow above rules recursively
        // if an editable text element of some sort was found, select it
        let newItem = _.get(this.element.elements, path);
        if (newItem && newItem.elements) {
            let text = findTextItem(newItem.elements);
            if (text) {
                ds.selection.element = text;
            }
        }
    },

    addItem: function(options = {}) {
        if (this.element.canvas.layouter.isGenerating) {
            return Promise.resolve();
        }

        options = _.defaults(options, { model: {}, transition: true });

        if (this.element.addItem && (this.element.maxItemCount ? (this.element.itemCount < this.element.maxItemCount) : true)) {
            let newItem = this.element.addItem(options.model || {});

            return this.element.canvas.updateCanvasModel(options.transition).then(() => {
                return newItem;
            }).catch(err => {
                console.log(err);
                ShowWarningDialog({
                    title: "Sorry, we can't make this change",
                    message: "We aren't able to fit another item to this layout.",
                });
            });
        }
    },

    _handleKeyboardShortcut(event) {
        switch (event.which) {
            case Key.KEY_D:
                this.addItem();
        }
    }
});

export const CollectionItemElementSelection = ElementSelection.extend({
    captureMouseEvents: true,
    showDragDropTarget: true,
    dragDistance: 5,
    dropTargetOverlapTreshold: 0.67,

    setup: function(options) {
        this.options = options;
    },

    getTitle: function() {
        return `${this.element.name} #${this.element.itemIndex + 1}`;
    },

    canDrag: function() {
        return this.element.canDrag && this.element.parentElement.itemCollection.length > 1;
    },

    getDragAxis: function() {
        return "";
    },

    canDelete: function() {
        return !app.tour && this.element.canDelete && this.element.parentElement.itemCollection.length > this.element.parentElement.minItemCount;
    },

    transitionOnDelete: function() {
        return true;
    },

    getOffset: function() {
        return 0;
    },

    onCheckDrag: function(event) {
        // don't start drag if we moused down on an ui button within the draggable element
        if (($(event.target).hasClass("button") && !$(event.target).hasClass("drag_button")) || $(event.target).closest(".control").length || $(event.target).hasClass("micon")) {
            return false;
        }

        // dont start drag when we are editing text or rolling over a text element (where the user can drag-select text)
        if (!$(event.target).hasClass("drag_button") && (app.isEditingText || ds.selection.rolloverElement instanceof TextElement || ds.selection.rolloverElement instanceof ContentElement)) {
            event.preventDefault();
            return false;
        }

        if (ds.selection.element != this.element) {
            ds.selection.element = this.element;
        }

        return true;
    },

    render() {
        ElementSelection.prototype.render.apply(this, arguments);

        if (this.canDrag()) {
            this.createDragWidget(this.element, {
                position: this.getDragWidgetPosition()
            });
        }
        this.setupGridDragging();

        // create the item delete button
        if (this.canDelete()) {
            this.createDeleteComponentWidget({
                target: this.element,
                action: () => this.onDeleteItem(),
                position: this.getDeleteButtonPosition()
            });
        }

        return this;
    },

    getDeleteButtonPosition() {
        return WidgetPositionType.DELETE_BUTTON;
    },

    onDeleteItem() {
        let containerElement = this.element.parentElement;
        if (containerElement instanceof CollectionElement && containerElement.itemCollection.length > containerElement.minItemCount) {
            containerElement.deleteItem(this.element.id);

            // note: we need to set the forceRender flag on delete so we can get out of layout does not fit states
            this.element.canvas.updateCanvasModel(this.transitionOnDelete(), true).then(() => {
                ds.selection.element = null;
            });
        } else {
            ShowWarningDialog({
                title: "Oops!",
                message: "Can't delete last item",
            });
        }
    },

    onStartDrag: function(event, dragProps, axis) {
        app.isDraggingItem = true;
        ds.selection.rolloverElement = null;
        dragProps.dragItem.isDragging = true;
        dragProps.dragItem.showDragDropTarget = this.showDragDropTarget;
        app.mainView.editorView.selectionLayer.hideWidgets($(".rollover_hilite, .drag_button"));
    },

    onDrag: function(event, position, dragProps) {
        dragProps.dragItem.dragPosition = new geom.Point(position.elementPosition.x, position.elementPosition.y);

        if (!this.canvas.layouter.isGenerating) {
            this.calcDropTarget(dragProps, position);
            if (this.element.canRefreshElement) {
                this.element.refreshElement();
            } else {
                this.canvas.refreshCanvas(false);
            }
        }

        this.options.onDrag && this.options.onDrag();
    },

    getDropTargets: function(containerElement) {
        return containerElement.elements;
    },

    calcDropScore: function(dragBounds, target, position) {
        switch (position.axis) {
            case "x":
                return dragBounds.intersection(target.bounds).width;
            case "y":
                return dragBounds.intersection(target.bounds).height;
            default:
                return dragBounds.intersection(target.bounds).area();
        }
    },

    calcDropOverlap: function(score, dragBounds, dropTarget, dragProps, position) {
        switch (position.axis) {
            case "x":
                return score > dropTarget.offsetBounds.width * this.dropTargetOverlapTreshold || score > dragBounds.width * this.dropTargetOverlapTreshold;
            case "y":
                return score > dropTarget.offsetBounds.height * this.dropTargetOverlapTreshold || score > dragBounds.height * this.dropTargetOverlapTreshold;
            default:
                return score > dropTarget.offsetBounds.area() * this.dropTargetOverlapTreshold || score > dragBounds.area() * this.dropTargetOverlapTreshold;
        }
    },

    calcDropTarget: function(dragProps, position) {
        if (this.canvas.layouter.isGenerating) return;

        let dragBounds = dragProps.dragItem.offsetBounds;
        dragBounds.left = position.elementPosition.x;
        dragBounds.top = position.elementPosition.y;

        let score = 0;
        let dropTarget;
        _.each(this.getDropTargets(dragProps.containerElement), target => {
            if (target instanceof BaseElement) {
                const overlap = this.calcDropScore(
                    dragBounds,
                    target,
                    position,
                );

                if (overlap > score && target !== dragProps.dragItem) {
                    score = overlap;
                    dropTarget = target;
                }
            }
        });

        if (score > 0 && dropTarget !== dragProps.dragItem) {
            const isOverlap = this.calcDropOverlap(
                score,
                dragBounds,
                dropTarget,
                dragProps,
                position,
            );

            if (isOverlap) {
                this.updateModelFromDropAction(dropTarget, dragProps);
            }
        }
    },

    updateModelFromDropAction: function(dropTarget, dragProps) {
        let dropTargetIndex = dropTarget.itemIndex;
        if (this.dropTargetIndex !== dropTargetIndex) {
            dragProps.containerElement.deleteItem(dragProps.dragItem.id);
            dragProps.containerElement.addItem(dragProps.dragItem.model, dropTargetIndex);
            this.dropTargetIndex = dropTargetIndex;
        }
    },

    onStopDrag: function(event, position, dragProps, options = { forceRender: false }) {
        event.stopPropagation();

        dragProps.dragItem.isDragging = false;
        dragProps.dragItem.canvas.updateCanvasModel(false, options.forceRender).then(() => {
            ds.selection.element = null;
            ds.selection.rolloverElement = this.element;
            ds.selection.element = this.element;
            app.mainView.editorView.selectionLayer.showWidgets();

            ds.selection.element.overlay && ds.selection.element.overlay.layout();
            this.options.onStopDrag && this.options.onStopDrag();
        });
    },

    setupGridDragging: function() {
        if (this.canDrag()) {
            let dragProps = {
                dragItem: this.element.findClosestOfType(CollectionItemElement),
                containerElement: this.element.findClosestOfType(CollectionElement)
            };

            this.$el.makeDraggable({
                element: dragProps.dragItem,
                axis: this.getDragAxis(),
                dragDistance: 5,
                check: event => this.onCheckDrag(event),
                start: (event, axis) => this.onStartDrag(event, dragProps, axis),
                drag: (event, position) => this.onDrag(event, position, dragProps),
                stop: (event, position) => this.onStopDrag(event, position, dragProps),
            });
        }
    },

});

const CollectionItemElementRollover = ElementRollover.extend({
    showSelectionBox: true
});

export const editors = {
    CollectionItemElementRollover
};
